import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, AlertCircle } from 'lucide-react';
import Footer from '../components/Footer';

const WarrantyPage = () => (
  <div className="privacy-page min-h-screen" data-testid="warranty-page">
    <Helmet>
      <title>Гарантія та повернення — CIMM Computers Одеса</title>
      <meta name="description" content="Умови гарантії на ремонт ноутбуків, телефонів, приставок від сервісного центру CIMM у м. Одеса. Терміни та умови повернення." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://cimm.od.ua/garantiya" />
    </Helmet>

    <header className="cyber-header fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="text-2xl font-bold neon-text">CIMM</div>
          <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
        </Link>
        <a href="tel:+380974178788" className="neon-text font-semibold">+38 097 417 8788</a>
      </div>
    </header>

    <section className="pt-32 pb-16 px-4 dark-section">
      <div className="container mx-auto max-w-4xl relative z-10">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-orange-500 icon-glow" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Гарантія та <span className="neon-text">повернення</span>
          </h1>
          <p className="text-gray-400">Прозорі умови без прихованих пунктів</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="glass-card p-6 text-center">
            <Clock className="w-10 h-10 mx-auto mb-2 text-orange-500" />
            <div className="text-3xl font-bold text-white">до 90</div>
            <div className="text-gray-400 text-sm">днів гарантії</div>
          </div>
          <div className="glass-card p-6 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-orange-500" />
            <div className="text-3xl font-bold text-white">100%</div>
            <div className="text-gray-400 text-sm">офіційна гарантія</div>
          </div>
          <div className="glass-card p-6 text-center">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-orange-500" />
            <div className="text-3xl font-bold text-white">0 ₴</div>
            <div className="text-gray-400 text-sm">повторний огляд</div>
          </div>
        </div>

        <div className="glass-card p-8 space-y-6 text-gray-300 leading-relaxed" data-testid="warranty-content">
          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Термін гарантії</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Заміна дисплея, тачскріна, скла — <strong>30 днів</strong></li>
              <li>Заміна акумулятора — <strong>90 днів</strong></li>
              <li>Ремонт материнської плати, чипів — <strong>30 днів</strong></li>
              <li>Чистка від пилу, заміна термопасти — <strong>30 днів</strong></li>
              <li>Встановлення ПЗ, оновлення — <strong>14 днів</strong></li>
              <li>Термопрокладки — <strong>гарантія 90 днів на охолодження</strong></li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Що покриває гарантія</h2>
            <p>
              Гарантія покриває неякісно виконаний ремонт та дефекти встановлених запчастин. У межах гарантійного
              терміну ми безкоштовно повторно виконаємо роботи або замінимо запчастину.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Що НЕ покриває гарантія</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Механічні пошкодження після ремонту (падіння, удари);</li>
              <li>Потрапляння вологи, окислення;</li>
              <li>Стороннє втручання після ремонту (розбирання, спроба самостійного ремонту);</li>
              <li>Пошкодження від нестабільної електромережі, стрибків напруги;</li>
              <li>Природний знос акумуляторів понад заявлені параметри виробника;</li>
              <li>Витратні матеріали (плівки, чохли).</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Як скористатися гарантією</h2>
            <ol className="list-decimal ml-6 space-y-1">
              <li>Зателефонуйте нам за номером <a href="tel:+380974178788" className="text-orange-400 hover:underline">+38 097 417 8788</a> та опишіть проблему.</li>
              <li>Принесіть пристрій та квитанцію про виконання робіт.</li>
              <li>Наш інженер проведе повторну діагностику <strong>безкоштовно</strong>.</li>
              <li>За гарантійним випадком ми виконаємо повторний ремонт без оплати.</li>
            </ol>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Повернення грошей</h2>
            <p>
              Якщо ми не змогли усунути несправність після повторного ремонту або визначили, що ремонт неможливий,
              ми повертаємо повну вартість робіт протягом 3 робочих днів. Вартість запчастин повертається за умови
              їх повернення у неушкодженому стані.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Контакти для гарантійних звернень</h2>
            <p>
              <strong>ФОП Войчук Дмитро Володимирович</strong><br />
              Телефон: <a href="tel:+380974178788" className="text-orange-400 hover:underline">+38 097 417 8788</a><br />
              Email: <a href="mailto:info@cimm.od.ua" className="text-orange-400 hover:underline">info@cimm.od.ua</a><br />
              Час роботи: Пн–Сб 11:00–18:00
            </p>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default WarrantyPage;
